import { flowRight, isNil } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from '../../../common/components/runtime-context';
import Page from '../../components/page';
import CategoryDescription from '../../components/category-description';
import ForumDescription from '../../components/forum-description';
import PostList from '../../components/post-list';
import {
  getSortedPostsByCategoryId,
  getPostsByCategoryIdAndPage,
  getSortedAllPosts,
} from '../../selectors/post-selectors';
import AccountSuspended from '../../components/account-suspended';
import Restriction from '../../components/restriction';
import RestrictionPrivateMember from '../../components/restriction-private-member';
import PageMetadata from '../../components/page-metadata';
import AnimatedLoader from '../../components/animated-loader';
import NotFound from '../../components/not-found';
import NoPosts from '../../components/no-posts';
import SubcategoryList from '../subcategory-list';
import { FullHeightLayout, SimpleLayout, EmptyLayout } from '../../components/layout';
import AppLoaded from '../../components/app-loaded';
import { POSTS_PER_PAGE } from '../../constants/pagination';
import { LAYOUT_TYPE_PINBOARD } from '../../constants/layout-types';
import withAuth from '../../hoc/with-auth';
import forDevice from '../../hoc/for-device';
import withDeviceType from '../../hoc/with-device-type';
import forDesktopOnly from '../../hoc/for-desktop-only';
import { getCategoryBySlug, getCategories } from '../../selectors/categories-selectors';
import { getIsCategoryLoading } from '../../selectors/is-loading-selectors';
import { getIsPostsLoaded } from '../../selectors/is-loaded-selectors';
import { getLayoutType } from '../../selectors/layout-selectors';
import { getCategoryPageSorting } from '../../selectors/sorting-selectors';
import { getRouteParams } from '../../../common/router/router-selectors';
import { getImageMetadataFromWixMedia } from '../../components/page-metadata/image-adapter';
import { FEED_PAGE } from '../../constants/page-names';
import { getEntityCount } from '../../selectors/pagination-selectors';
import { getIsHeaderEnabled } from '../../selectors/app-settings-selectors';
import { isMemberAreaActive } from '../../selectors/member-area-selectors';
import getOuterUrl from '../../services/get-outer-url';
import { getLastPage } from '../../services/pagination';
import { UNCATEGORIZED_POSTS } from '../../reducers/pagination';
import LargeSpacingLayout from '../../components/layout/large-spacing-layout';

const Layout = forDevice(EmptyLayout, LargeSpacingLayout);
const CategoryDescriptionDesktopOnly = forDesktopOnly(CategoryDescription);
const ForumDescriptionDesktopOnly = forDesktopOnly(ForumDescription);
const NotFoundLayout = forDevice(FullHeightLayout, SimpleLayout);

const resetPage = () => ({ page: 1 });
const incrementPage = state => ({ page: state.page + 1 });
const isCategoryNotFound = (c, categorySlug) => categorySlug !== '' && c._id === null;

export class CategoryPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isAnimated: !props.allPosts.length,
      page: props.layoutType !== LAYOUT_TYPE_PINBOARD ? props.page : 1,
    };
  }

  componentDidMount() {
    const { category } = this.props;
    this.props.pageOpened({
      page: FEED_PAGE,
      category,
    });
    this.props.emitOpenCategory(category);
  }

  componentWillUnmount() {
    const { category } = this.props;
    this.props.emitCloseCategory(category);
  }

  componentWillReceiveProps({ categorySlug, category, newPostId, page, sortingType }) {
    const currentCategorySlug = this.props.categorySlug;

    if (currentCategorySlug !== categorySlug) {
      this.props.emitCloseCategory(this.props.category);
      this.props.emitOpenCategory(category);
      this.setState(resetPage, () => this.fetchPosts(category._id));
    } else if (this.state.page !== page && this.props.page !== page) {
      this.setState({ page }, () => this.fetchPosts(category._id));
    } else if (
      (this.props.layoutType === LAYOUT_TYPE_PINBOARD || this.props.isMobile) &&
      this.props.sortingType !== sortingType
    ) {
      this.setState(resetPage);
    }

    if (newPostId && newPostId !== this.props.newPostId) {
      const postElement = document.getElementById(newPostId);
      if (!postElement) {
        return;
      }
    }
  }

  componentDidUpdate({ isAuthenticated, isBlocked, isPrivate }) {
    if (
      this.props.isAuthenticated !== isAuthenticated ||
      this.props.isBlocked !== isBlocked ||
      this.props.isPrivate !== isPrivate
    ) {
      this.fetchPosts();
    }
  }

  fetchPosts = (categoryId = this.props.category._id, page = this.state.page) => {
    if (!this.shouldShowPosts()) {
      return;
    }

    this.props.fetchPosts({
      categoryId,
      page,
      pageSize: POSTS_PER_PAGE,
    });
  };

  shouldShowPosts() {
    const {
      category,
      isAuthenticated,
      isBlocked,
      isPrivate,
      isMemberAreaActive,
      showAllPosts,
    } = this.props;
    if (showAllPosts) {
      return true;
    }

    const hasPrivateSocialProfile = isMemberAreaActive && isPrivate;
    const canSeePrivateCategory = isAuthenticated && !isBlocked && !hasPrivateSocialProfile;
    return !category.isPrivate || canSeePrivateCategory;
  }

  changePage = page => {
    this.props.navigateWithinForum(this.buildPageUrl(page));
  };

  handleLoad = () => this.setState(incrementPage, this.fetchPosts);

  renderPosts() {
    const {
      isBlocked,
      currentPagePosts,
      allPosts,
      entityCount,
      category,
      isLoading,
      isPostsLoaded,
      isAuthenticated,
      isPrivate,
      isMemberAreaActive,
      categorySlug,
    } = this.props;
    const hasPosts = allPosts.length;

    if (isBlocked) {
      return <AccountSuspended />;
    } else if (category.isPrivate && !isAuthenticated) {
      return <Restriction />;
    } else if (category.isPrivate && isMemberAreaActive && isPrivate) {
      return <RestrictionPrivateMember />;
    } else if (!hasPosts) {
      return <NoPosts category={category} />;
    } else {
      return (
        <PostList
          page={this.props.page}
          entityCount={entityCount}
          allPosts={allPosts}
          changePage={this.changePage}
          buildPageUrl={this.buildPageUrl}
          loadMore={this.handleLoad} // eslint-disable-line react/jsx-handler-names
          currentPagePosts={currentPagePosts}
          isLoading={isLoading && currentPagePosts.length === 0}
          showLoaderInLoadMore={isLoading}
          category={category}
          isLoaded={isPostsLoaded}
          selectedCategorySlug={categorySlug}
        />
      );
    }
  }

  buildPagingData = () => {
    const { page, entityCount } = this.props;
    const lastPage = getLastPage(entityCount, POSTS_PER_PAGE);
    const prev = page > 1 ? page - 1 : undefined;
    const next = page < lastPage ? page + 1 : undefined;
    return {
      current: page,
      next,
      prev,
      total: lastPage,
    };
  };

  buildPageUrl = (page = 1) => {
    const { categorySlug } = this.props;
    const isFirstPage = page === 1;
    if (categorySlug) {
      return isFirstPage ? `/${categorySlug}` : `/${categorySlug}/p-${page}`;
    } else {
      return isFirstPage ? `/` : `/p-${page}`;
    }
  };

  renderPageMetadata = () => {
    const { category, page } = this.props;
    return (
      <PageMetadata
        title={category.label}
        description={category.description}
        image={getImageMetadataFromWixMedia(category.cover)}
        page={page}
        entity={category}
        entityType={'category'}
        refersTo={category.slug}
        paging={this.buildPagingData()}
      />
    );
  };

  renderPageDescription = () => {
    const { showAllPosts, category, isHeaderEnabled } = this.props;
    if (showAllPosts) {
      return <ForumDescriptionDesktopOnly />;
    }
    return <CategoryDescriptionDesktopOnly category={category} isCoverVisible={isHeaderEnabled} />;
  };

  render() {
    const {
      category,
      isLoading,
      allPosts,
      isPostsLoaded,
      isMobileDesignUpdatesEnabled,
      showAllPosts,
      categorySlug,
    } = this.props;
    if (isCategoryNotFound(category, categorySlug)) {
      return (
        <Page>
          <NotFoundLayout>
            <NotFound />
          </NotFoundLayout>
        </Page>
      );
    }
    return (
      <Page noSpacing={isMobileDesignUpdatesEnabled}>
        <Layout>
          {this.renderPageMetadata()}
          {this.renderPageDescription()}
          {!showAllPosts && <SubcategoryList categoryId={category._id} />}
          <AnimatedLoader
            isLoading={isLoading && allPosts.length === 0}
            isAnimated={this.state.isAnimated}
          >
            {this.renderPosts()}
          </AnimatedLoader>
        </Layout>
        {isPostsLoaded && <AppLoaded />}
      </Page>
    );
  }
}

CategoryPage.propTypes = {
  categorySlug: PropTypes.string,
  fetchPosts: PropTypes.func.isRequired,
  emitOpenCategory: PropTypes.func.isRequired,
  emitCloseCategory: PropTypes.func.isRequired,
  pageOpened: PropTypes.func.isRequired,
  categoryId: PropTypes.string,
  location: PropTypes.object,
  setIsLoaded: PropTypes.func.isRequired,
  navigateWithinForum: PropTypes.func,
  entityCount: PropTypes.number,
  page: PropTypes.number,
  allPosts: PropTypes.array,
  currentPagePosts: PropTypes.array,
  category: PropTypes.object,
  isAuthenticated: PropTypes.bool,
  isBlocked: PropTypes.bool,
  isPrivate: PropTypes.bool,
  params: PropTypes.object,
  t: PropTypes.func,
  newPostId: PropTypes.string,
  isLoading: PropTypes.bool,
  isPostsLoaded: PropTypes.bool,
  layoutType: PropTypes.number,
  isMemberAreaActive: PropTypes.bool,
  isHeaderEnabled: PropTypes.bool,
  isMobileDesignUpdatesEnabled: PropTypes.bool,
  showAllPosts: PropTypes.bool.isRequired,
  sortingType: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

const mapRuntimeToProps = (state, ownProps, actions) => {
  const params = getRouteParams(state);
  const getRouteCategory = () => getCategoryBySlug(state, params.categorySlug) || { _id: null };
  const page = parseInt(params.page, 10) || 1;
  const category = getRouteCategory();
  const categorySlug = category.slug;
  const categoryId = category._id;
  const showAllPosts = categoryId === null && isNil(categorySlug);

  return {
    categorySlug: categorySlug || '',
    category,
    allPosts: showAllPosts
      ? getSortedAllPosts(state)
      : getSortedPostsByCategoryId(state, categoryId),
    currentPagePosts: getPostsByCategoryIdAndPage(
      state,
      page,
      showAllPosts ? UNCATEGORIZED_POSTS : categoryId,
    ),
    newPostId: state.newPostMessage,
    entityCount: getEntityCount(state, 'posts', categoryId || UNCATEGORIZED_POSTS),
    categoryId,
    isLoading: getIsCategoryLoading(state, categoryId),
    isPostsLoaded: getIsPostsLoaded(state, categoryId),
    isMemberAreaActive: isMemberAreaActive(state),
    page,
    isHeaderEnabled: getIsHeaderEnabled(state),
    layoutType: getLayoutType(state),
    sortingType: getCategoryPageSorting(state),
    showAllPosts,
    fetchPosts: actions.fetchCategoryPosts,
    setIsLoaded: actions.setIsLoaded,
    pageOpened: actions.pageOpened,
    navigateWithinForum: actions.navigateWithinForum,
    emitOpenCategory: actions.emitOpenCategory,
    emitCloseCategory: actions.emitCloseCategory,
  };
};

export default flowRight(
  connect(mapRuntimeToProps),
  withAuth,
  withDeviceType,
)(CategoryPage);
